// src/autodesk_usage/pages/ProductDashboardPage.tsx
// All comments in English.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProductSearch } from "../hooks/useProductSearch";
import { useProductTopHolders } from "../hooks/useProductTopHolders";
import SectionCard from "@/components/shared/SectionCard";
import SectionHeader from "@/components/shared/SectionHeader";
import SharedTable from "@/components/shared/SharedTable";
import RiskLevelBadge from "@/autodesk_usage/components/shared/RiskLevelBadge";
import TrendIndicator from "@/autodesk_usage/components/shared/TrendIndicator";
import LoadingSpinner from "@/components/shared/LoadingSpinner";
import EmptyState from "@/components/shared/EmptyState";
import ErrorState from "@/components/shared/ErrorState";
import SalesVsUsageChart from "@/autodesk_usage/components/shared/chart/SalesVsUsageChart";
import ProductSelect from "@/autodesk_usage/components/product_dashboard/ProductSelect";
import { useQuery } from "@tanstack/react-query";
import {
  getTopChurnRiskCustomers,
  getRiskProducts,
  getSalesVsUsageSeries,
} from "../api/risk";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Info } from "lucide-react";

type Segment = "All" | "Shrinking" | "Flow to Competitor";

function BehaviorHelpDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Info className="h-4 w-4" />
          About Behaviors &amp; Metrics
        </Button>
      </DialogTrigger>

      {/* Wide dialog with scrollable body */}
      <DialogContent className="w-[min(96vw,980px)] max-w-none">
        <DialogHeader>
          <DialogTitle>Company Behavior Definitions</DialogTitle>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto space-y-8 pr-1 text-sm leading-6">
          {/* Behaviors */}
          <section>
            <h3 className="mb-2 text-base font-semibold">Behaviors</h3>
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full table-auto text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-sm font-medium text-gray-700">Behavior</th>
                    <th className="px-3 py-2 text-sm font-medium text-gray-700">Definition</th>
                    <th className="px-3 py-2 text-sm font-medium text-gray-700">Typical signals</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2 font-medium">Shrinking</td>
                    <td className="px-3 py-2">
                      Sustained decrease in usage and/or license holdings for the selected product.
                    </td>
                    <td className="px-3 py-2">
                      <ul className="list-disc pl-5 space-y-1">
                        <li><code>slope_4m</code> &lt; 0 (downward trend in recent months)</li>
                        <li>Drop in <code>active_seats</code> and/or <code>used_users</code></li>
                        <li>Lower <code>ur_cap</code> (utilization) compared to prior period</li>
                      </ul>
                    </td>
                  </tr>

                  <tr>
                    <td className="px-3 py-2 font-medium">Flow to Competitor</td>
                    <td className="px-3 py-2">
                      Usage or seat reduction patterns that indicate substitution by competitor tools.
                    </td>
                    <td className="px-3 py-2">
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Seats reduced while workload persists elsewhere (external signals)</li>
                        <li>Stable/declining <code>used_users</code> with decreasing <code>active_seats</code></li>
                        <li>Behavior label provided by backend classification</li>
                      </ul>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Metrics */}
          <section>
            <h3 className="mb-2 text-base font-semibold">Key Metrics in Tables</h3>
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full table-auto text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-sm font-medium text-gray-700">Metric</th>
                    <th className="px-3 py-2 text-sm font-medium text-gray-700">Meaning</th>
                    <th className="px-3 py-2 text-sm font-medium text-gray-700">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-3 py-2 font-medium">Active Seats</td>
                    <td className="px-3 py-2">Number of active licenses (holders) for the product.</td>
                    <td className="px-3 py-2">Used to rank Top Holders.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Used Users</td>
                    <td className="px-3 py-2">Unique users who actually used the product.</td>
                    <td className="px-3 py-2">Paired with Active Seats to derive utilization.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">UR Cap</td>
                    <td className="px-3 py-2">
                      Capped utilization rate. Typical formula: <code>min(used_users / active_seats, 1.0)</code>.
                    </td>
                    <td className="px-3 py-2">Higher is better; values close to 1.0 indicate high coverage.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Slope 4m</td>
                    <td className="px-3 py-2">
                      Recent usage trend over the last months (linear slope).
                    </td>
                    <td className="px-3 py-2">
                      Negative values suggest shrinking, positive values suggest growth.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Risk</td>
                    <td className="px-3 py-2">Categorical risk level: <code>High</code> / <code>Medium</code> / <code>Low</code>.</td>
                    <td className="px-3 py-2">Derived from multiple indicators.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-medium">Risk Score</td>
                    <td className="px-3 py-2">
                      Final risk score (e.g., 0–100) from backend calculation.
                    </td>
                    <td className="px-3 py-2">Used to sort Top Churn-Risk customers.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Classification logic (high-level) */}
          <section>
            <h3 className="mb-2 text-base font-semibold">How items are ranked</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Top 10 Highest Churn-Risk</strong> are returned from backend already sorted
                by final risk score (descending).
              </li>
              <li>
                Behavior tabs (<em>All / Shrinking / Flow to Competitor</em>) filter results by the
                backend-provided <code>behavior_label</code>.
              </li>
              <li>
                <strong>Top Holders</strong> are sorted by <code>active_seats</code> (descending).
              </li>
            </ul>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}


export default function ProductDashboardPage() {
  const navigate = useNavigate();
  const { options } = useProductSearch("");
  const [segment, setSegment] = useState<Segment>("All");
  const [product, setProduct] = useState<string | null>(null);

  const topHoldersQuery = useProductTopHolders(product as string, 10, "-active_seats");

  const topChurnQuery = useQuery({
    queryKey: ["topChurn", product, segment],
    queryFn: () =>
      segment === "All"
        ? getTopChurnRiskCustomers({
            product: product as string,
            page_size: 10,
          })
        : getRiskProducts({
            product: product as string,
            page_size: 10,
            page: 1,
            sort: "-risk_score_final",
            behavior: segment,
          }),
    enabled: !!product,
  });

  const seriesQuery = useQuery({
    queryKey: ["series", product],
    queryFn: () => getSalesVsUsageSeries({ product: product as string, months: 12 }),
    enabled: !!product,
  });

  const rows = topChurnQuery.data?.items ?? [];

  const handleRowClick = (row: any) => {
    const name: string = row?.company_name || "";
    if (!name) return;
    const encoded = encodeURIComponent(name);
    navigate(`/autodesk-usage/customers/${encoded}`);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      <header className="space-y-2">
        <h1 className="text-xl font-semibold">Product Dashboard</h1>
        <p className="text-sm text-gray-600">
          Select a product to view risk and trends. The product selector supports type-in filtering.
        </p>
      </header>

      <div className="mb-4">
        <ProductSelect
          value={product}
          onChange={(p) => setProduct(p)}
          placeholder="Search product..."
          autoFocus
        />
      </div>

      <SectionCard>
        <SectionHeader title="Sales vs Usage (Monthly)" />
        {!product ? (
          <EmptyState text="Please select a product." />
        ) : seriesQuery.isLoading ? (
          <LoadingSpinner />
        ) : seriesQuery.isError ? (
          <ErrorState text="Failed to load series." />
        ) : (
          <SalesVsUsageChart data={seriesQuery.data ?? []} />
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader
          title="Top 10 Highest Churn-Risk Customers"
          subtitle="Segmented by behavior"
          right={
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-xl border border-gray-200 bg-white p-1">
                {(["All", "Shrinking", "Flow to Competitor"] as Segment[]).map((tab) => {
                  const active = segment === tab;
                  return (
                    <button
                      key={tab}
                      onClick={() => setSegment(tab)}
                      className={[
                        "min-w-[96px] rounded-lg px-3 py-1 text-sm transition",
                        active
                          ? "bg-indigo-600 text-white shadow"
                          : "text-gray-700 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      {tab}
                    </button>
                  );
                })}
              </div>
              <BehaviorHelpDialog />
            </div>
          }
        />
        {!product ? (
          <EmptyState text="Please select a product." />
        ) : topChurnQuery.isLoading ? (
          <LoadingSpinner />
        ) : topChurnQuery.isError ? (
          <ErrorState text="Failed to load risk list." />
        ) : rows.length === 0 ? (
          <EmptyState text="No data" />
        ) : (
          <SharedTable
            rows={rows}
            cols={[
              { key: "company_name", header: "Company" },
              {
                key: "behavior_label",
                header: "Behavior",
                render: (r: any) => r.behavior_label || "-",
              },
              { key: "active_seats", header: "Active Seats" },
              { key: "used_users", header: "Used Users" },
              {
                key: "ur_cap",
                header: "UR Cap",
                render: (r: any) =>
                  r.ur_cap != null ? Number(r.ur_cap).toFixed(2) : "-",
              },
              {
                key: "slope_4m",
                header: "Slope 4m",
                render: (r: any) => <TrendIndicator value={Number(r.slope_4m || 0)} />,
              },
              {
                key: "risk_level",
                header: "Risk",
                render: (r: any) => <RiskLevelBadge level={r.risk_level} />,
              },
              {
                key: "risk_score_final",
                header: "Risk Score",
                render: (r: any) =>
                  r.risk_score_final != null ? Number(r.risk_score_final).toFixed(0) : "-",
              },
            ]}
            onRowClick={handleRowClick}
          />
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader
          title="Top Holders (by Active Seats)"
          subtitle="Top 10 customers with the most seats"
        />
        {!product ? (
          <EmptyState text="Please select a product." />
        ) : topHoldersQuery.isLoading ? (
          <LoadingSpinner />
        ) : topHoldersQuery.isError ? (
          <ErrorState text="Failed to load top holders." />
        ) : (
          <SharedTable
            rows={topHoldersQuery.data?.items ?? []}
            cols={[
              { key: "company_name", header: "Company" },
              { key: "active_seats", header: "Active Seats" },
              { key: "used_users", header: "Used Users" },
              {
                key: "ur_cap",
                header: "UR Cap",
                render: (r: any) =>
                  r.ur_cap != null ? Number(r.ur_cap).toFixed(2) : "-",
              },
              {
                key: "risk_level",
                header: "Risk",
                render: (r: any) => <RiskLevelBadge level={r.risk_level} />,
              },
            ]}
            onRowClick={handleRowClick}
          />
        )}
      </SectionCard>
    </div>
  );
}
